import { Card, Select, Typography } from 'antd';
import { TimeUnit } from 'chart.js';
import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { DashboardProps, ExtendedDataProvider } from '../../types';
import ChangeInstanceLogWidget from './ChangeInstanceLogWidget';
import DashboardMetrics from './DashboardMetrics';
import FlowbiteUnifiedBarChart from './FlowbiteUnifiedLineChart';
import useGetGraphStatistics, { filterKey } from './useGetGraphStatistics';
import GeneralFilter from '../ChangeInstances/GeneralFilter';
import './Dashboard.css';

const { Option } = Select;
const { Title, Text } = Typography;

const BaseDashboard = ({ pov, applicationsResource, teamsResource, servicesResource, statisticsResource, allTeams = false }: DashboardProps) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [basics, setBasics] = useState<any>(null);
    const [basicsLoading, setBasicsLoading] = useState(true);
    const [basicsError, setBasicsError] = useState<string | null>(null);
    const [filterValues, setFilterValues] = useState<any>({});
    const {
        setGraphIntervalUnit,
        processedChangesGraphData,
        avgTimeCompletionGraphData,
        dataUnit,
        isLoaded: chartLoaded,
        error: chartError,
    } = useGetGraphStatistics(filterValues, setFilterValues, pov, allTeams);
    const filters = filterKey(filterValues);

    useEffect(() => {
        let current = true;
        setBasicsLoading(true);
        setBasicsError(null);
        dataProvider.getAction(statisticsResource, {
            verb: 'basics',
            filter: { ...filterValues, ...(allTeams ? { all_teams: true } : {}) },
        })
            .then((response: any) => {
                if (!current) return;
                setBasics(response.data);
                setBasicsLoading(false);
            })
            .catch((error) => {
                console.error(error);
                if (!current) return;
                // the figures used to sit at their spinner for ever when this
                // failed; nothing cleared the loading state
                setBasicsError('Figures could not be loaded.');
                setBasicsLoading(false);
            });
        // a filter changed while the previous request was in flight: its answer
        // is about the old filter and must not land
        return () => { current = false; };
    }, [filters, pov, allTeams, statisticsResource]);

    /* Admin and Service Owner share a `pov`, so without `allTeams` here the
       filters set for one team would survive the switch to every team — and a
       team filter carried into Consumer means nothing there either. */
    useEffect(() => {
        setFilterValues({});
    }, [pov, allTeams]);

    return (
        /* A real full-height flex column. This was an antd `Space`, which is an
           inline-flex box that shrinks to its content — so the viewport row's
           `flex: 1` had nothing to stretch against and the page stopped
           wherever the content did, leaving the rest of a large screen blank. */
        <div className="netorca-dash">
            <div className="dash-filters">
                <span className="dash-filters__label">
                    <FontAwesomeIcon icon={faFilter} aria-hidden="true" />
                    Filter
                </span>
                <GeneralFilter
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    /* Each point of view drops the filter that says nothing
                       from where it stands: a consumer is already the team, so
                       a team filter would have one value; a service owner
                       reaches applications through the consumer that owns them.
                       Across every team neither is implied any more, so Admin
                       draws all three. */
                    applicationsResource={allTeams || pov === 'CONSUMER' ? applicationsResource : undefined}
                    teamsResource={allTeams || pov !== 'CONSUMER' ? teamsResource : undefined}
                    servicesResource={servicesResource}
                    allTeams={allTeams}
                    /* `flex: 1` here was harmless while the page was an antd
                       `Space`; in a real flex column it makes the filter bar
                       grow to a share of the page height and pushes its own
                       controls off the top. It takes the height it needs. */
                    style={{ flex: 1, padding: 0, marginBottom: 0, width: 'auto' }}
                    stateFilterEnabled={false}
                    changeTypeFilterEnabled={false}
                />
            </div>

            {/* Two columns for the whole page below the filters, so the history
                rail starts level with the figures rather than under them and
                runs the full height of the page beside everything else. */}
            <div className="dash-body">
                <div className="dash-left">
                    {basicsError ? (
                        <p className="dash-note dash-note--bad" role="alert">{basicsError}</p>
                    ) : (
                        <DashboardMetrics data={basics} loading={basicsLoading} pov={pov} />
                    )}

                    <Card className="dash-chart">
                        <div className="dash-chart__head">
                            <div>
                                <Title level={5} className="dash-chart__title">Change instances metrics</Title>
                                <Text type="secondary">
                                    Average completion time and number of completed changes over time
                                </Text>
                            </div>
                            <Select
                                defaultValue="month"
                                className="filter-select dash-chart__unit"
                                aria-label="Interval"
                                onChange={(value: TimeUnit) => setGraphIntervalUnit(value)}
                            >
                                <Option key={0} value="month">Monthly</Option>
                                <Option key={1} value="week">Weekly</Option>
                                <Option key={2} value="day">Daily</Option>
                            </Select>
                        </div>
                        <div className="dash-chart__body">
                            <FlowbiteUnifiedBarChart
                                avgTimeData={avgTimeCompletionGraphData}
                                changesData={processedChangesGraphData}
                                unit={dataUnit}
                                loading={!chartLoaded}
                                error={chartError}
                            />
                        </div>
                    </Card>
                </div>

                <Card className="dash-rail">
                    <ChangeInstanceLogWidget pov={pov} filterValues={filterValues} allTeams={allTeams} />
                </Card>
            </div>
        </div>
    );
};

export default BaseDashboard;
